/**
 * Radiology module — shared schemas and pure helpers.
 *
 * Mirrors the lab module shape: enums for modality / contrast / status,
 * input + output schemas for orders / reports / images, plus a tiny pure
 * helper for "does this impression text look critical?" which fuels the
 * critical-findings notification flow (added in a later sprint).
 */
import { z } from "zod";
import { bilingualStringSchema } from "./bilingual.ts";
import { paymentMethodSchema } from "./billing.ts";
import {
	criticalAlertStatusSchema,
	notificationMethodSchema,
} from "./clinical.ts";

// DICOM modality codes we surface in the order UI. Closed set — bumping
// it requires a DB CHECK migration to keep the database honest.
export const radiologyModalitySchema = z.enum([
	"XR", // Plain X-ray
	"CT", // Computed Tomography
	"MR", // Magnetic Resonance
	"US", // Ultrasound
	"MG", // Mammography
	"NM", // Nuclear Medicine
	"PT", // PET
	"BD", // Bone Density (DEXA)
	"FL", // Fluoroscopy
	"DX", // Digital Radiography (subset of XR but kept distinct for stats)
	"OT", // Other
]);
export type RadiologyModality = z.infer<typeof radiologyModalitySchema>;

/** Human-friendly Arabic+English labels for each modality, used by the UI. */
export const MODALITY_LABELS: Record<
	RadiologyModality,
	{ ar: string; en: string }
> = {
	XR: { ar: "أشعة سينية", en: "X-Ray" },
	CT: { ar: "مفراس", en: "CT" },
	MR: { ar: "رنين مغناطيسي", en: "MRI" },
	US: { ar: "سونار", en: "Ultrasound" },
	MG: { ar: "ماموغرام", en: "Mammography" },
	NM: { ar: "طب نووي", en: "Nuclear Medicine" },
	PT: { ar: "بت سكان", en: "PET" },
	BD: { ar: "كثافة عظام", en: "Bone Density" },
	FL: { ar: "تنظير شعاعي", en: "Fluoroscopy" },
	DX: { ar: "أشعة رقمية", en: "Digital X-Ray" },
	OT: { ar: "أخرى", en: "Other" },
};

export const radiologyContrastSchema = z.enum(["none", "oral", "iv", "both"]);
export type RadiologyContrast = z.infer<typeof radiologyContrastSchema>;
// Payment method reused from billing (cash | card | transfer | online | other)
// for the cash-basis daily revenue report.

export const radiologyUrgencySchema = z.enum(["routine", "stat"]);
export type RadiologyUrgency = z.infer<typeof radiologyUrgencySchema>;

export const radiologyOrderStatusSchema = z.enum([
	"pending",
	"in_progress",
	"reported",
	"delivered",
	"cancelled",
	"rejected",
]);
export type RadiologyOrderStatus = z.infer<typeof radiologyOrderStatusSchema>;

// 9 reasons mirror the lab specimen-rejection vocabulary so the UI can reuse
// the same dropdown component. Radiology adds 'contrast_contraindication'
// for studies aborted after the IV line is up.
export const radiologyRejectionReasonSchema = z.enum([
	"patient_no_show",
	"patient_pregnant",
	"patient_unfit",
	"contrast_contraindication",
	"technical_failure",
	"wrong_study",
	"wrong_patient",
	"equipment_down",
	"other",
]);
export type RadiologyRejectionReason = z.infer<
	typeof radiologyRejectionReasonSchema
>;

// ===== Order =====

export const createRadiologyOrderInputSchema = z.object({
	patient_id: z.string().uuid(),
	doctor_id: z.string().uuid().optional(),
	referral_doctor_id: z.string().uuid().nullable().optional(),
	modality: radiologyModalitySchema,
	body_region: z.string().min(1).max(120),
	study_name: z.string().min(1).max(160),
	contrast: radiologyContrastSchema.default("none"),
	urgency: radiologyUrgencySchema.default("routine"),
	clinical_info: z.string().max(2_000).nullable().optional(),
	total_price: z.string().optional(),
	/** Payment method captured for the daily revenue report (walk-ins). */
	payment_method: paymentMethodSchema.optional(),
	/** Scheduled scan time (slot booking). Omitted = ad-hoc walk-in. */
	scheduled_at: z.string().datetime().optional(),
	notes_ar: z.string().max(2_000).nullable().optional(),
	notes_en: z.string().max(2_000).nullable().optional(),
	// Cross-tenant referral: route this study to a linked radiology center.
	// The center performs it and the report flows back to this clinic's tenant.
	// Validated against an ACTIVE facility_link (partner_type='radiology').
	routed_to_radiology_id: z.string().uuid().nullable().optional(),
});
export type CreateRadiologyOrderInput = z.infer<
	typeof createRadiologyOrderInputSchema
>;

// ===== Facility daily revenue (cash-basis, lab + radiology) =====
// One report serves both facility types: the server picks lab_orders or
// radiology_orders by the caller's facility_type. Revenue = priced,
// non-cancelled orders in the window; method 'unspecified' buckets null.

export const facilityRevenueQuerySchema = z.object({
	from: z.string().datetime(),
	to: z.string().datetime(),
});
export type FacilityRevenueQuery = z.infer<typeof facilityRevenueQuerySchema>;

export const revenueRowSchema = z.object({
	id: z.string().uuid(),
	patient_name: z.string().nullable(),
	label: z.string(), // study name (radiology) or test summary (lab)
	accession_number: z.string().nullable(),
	total: z.string(),
	payment_method: z.string().nullable(),
	status: z.string(),
	at: z.string().datetime(),
});
export type RevenueRow = z.infer<typeof revenueRowSchema>;

export const facilityRevenueSchema = z.object({
	count: z.number().int(),
	gross: z.string(),
	by_method: z.array(
		z.object({
			method: z.string(),
			count: z.number().int(),
			total: z.string(),
		}),
	),
	rows: z.array(revenueRowSchema),
});
export type FacilityRevenueApi = z.infer<typeof facilityRevenueSchema>;

/** Record/adjust the payment method on a priced order (collect at counter). */
export const setOrderPaymentInputSchema = z.object({
	payment_method: paymentMethodSchema.nullable(),
});
export type SetOrderPaymentInput = z.infer<typeof setOrderPaymentInputSchema>;

export const radiologyOrderSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	doctor_id: z.string().uuid().nullable(),
	referral_doctor_id: z.string().uuid().nullable(),
	referral_pct: z.string().nullable(),
	routed_to_radiology_id: z.string().uuid().nullable(),
	pickup_code: z.string().nullable(),
	accession_number: z.string().nullable(),
	modality: radiologyModalitySchema,
	body_region: z.string(),
	study_name: z.string(),
	contrast: radiologyContrastSchema,
	urgency: radiologyUrgencySchema,
	clinical_info: z.string().nullable(),
	status: radiologyOrderStatusSchema,
	total_price: z.string().nullable(),
	payment_method: paymentMethodSchema.nullable().optional(),
	scheduled_at: z.string().nullable().optional(),
	recall_at: z.string().nullable().optional(),
	rejection_reason: radiologyRejectionReasonSchema.nullable(),
	rejection_notes: z.string().nullable(),
	rejected_at: z.string().nullable(),
	completed_at: z.string().nullable(),
	delivered_at: z.string().nullable(),
	notes_ar: z.string().nullable(),
	notes_en: z.string().nullable(),
	// Referral ownership: when this study was created by accepting a referral.
	source_referral_order_id: z.string().uuid().nullable().optional(),
	referring_clinic_name: z.string().nullable().optional(),
	referring_doctor_name: z.string().nullable().optional(),
	/** Commission % snapshot owed to the referring clinic (frozen at accept). */
	referral_commission_pct: z.string().nullable().optional(),
	share_token: z.string(),
	ordered_at: z.string(),
	// Joined patient name for the worklist (avoids an extra round trip per row).
	patient_name: z.string().optional(),
});
export type RadiologyOrderApi = z.infer<typeof radiologyOrderSchema>;

export const updateRadiologyOrderStatusInputSchema = z.object({
	status: z.enum(["in_progress", "cancelled", "delivered"]),
});
export type UpdateRadiologyOrderStatusInput = z.infer<
	typeof updateRadiologyOrderStatusInputSchema
>;

export const rejectRadiologyOrderInputSchema = z.object({
	reason: radiologyRejectionReasonSchema,
	notes: z.string().max(1_000).optional(),
});
export type RejectRadiologyOrderInput = z.infer<
	typeof rejectRadiologyOrderInputSchema
>;

// ===== Report =====

export const radiologyDiagnosisCodeSchema = z.object({
	code: z.string().min(1).max(20),
	label: z.string().min(1).max(200),
});
export type RadiologyDiagnosisCode = z.infer<
	typeof radiologyDiagnosisCodeSchema
>;

export const upsertRadiologyReportInputSchema = z.object({
	technique: z.string().max(4_000).nullable().optional(),
	comparison: z.string().max(2_000).nullable().optional(),
	findings: z.string().max(16_000).nullable().optional(),
	impression: z.string().max(8_000).nullable().optional(),
	recommendations: z.string().max(4_000).nullable().optional(),
	diagnosis_codes: z.array(radiologyDiagnosisCodeSchema).max(20).optional(),
	/**
	 * Radiologist's explicit critical declaration — independent of the keyword
	 * detector. Editable (set/unset) until finalize; on finalize it feeds the
	 * same critical-finding log + urgent-notification path as detections.
	 */
	manual_critical: z.boolean().optional(),
	manual_critical_reason: z.string().max(500).nullable().optional(),
	/**
	 * Optimistic lock (F1.3): the report version the client loaded. When present
	 * and it no longer matches the stored draft (another radiologist saved first),
	 * the upsert is rejected with 409 instead of silently clobbering their edit.
	 * Optional because system writers (finalize, referral copy) legitimately save
	 * without a user-loaded version; the interactive editor always sends it.
	 */
	expected_version: z.number().int().optional(),
});
export type UpsertRadiologyReportInput = z.infer<
	typeof upsertRadiologyReportInputSchema
>;

export const finalizeRadiologyReportInputSchema =
	upsertRadiologyReportInputSchema.extend({
		// Forces clients to confirm — protects against finalizing an empty draft.
		confirm: z.literal(true),
	});
export type FinalizeRadiologyReportInput = z.infer<
	typeof finalizeRadiologyReportInputSchema
>;

export const radiologyReportAddendumInputSchema = z.object({
	addendum: z.string().min(1).max(8_000),
});
export type RadiologyReportAddendumInput = z.infer<
	typeof radiologyReportAddendumInputSchema
>;

// ===== AI report assist (findings → impression + recommendations draft) =====

export const radiologyAssistInputSchema = z.object({
	modality: radiologyModalitySchema,
	body_region: z.string().max(120).optional(),
	clinical_info: z.string().max(2_000).optional(),
	technique: z.string().max(4_000).optional(),
	/** The radiologist's findings — the model drafts impression/recs from these. */
	findings: z.string().min(1).max(16_000),
});
export type RadiologyAssistInput = z.infer<typeof radiologyAssistInputSchema>;

export const radiologyAssistResultSchema = z.object({
	impression: z.string(),
	recommendations: z.string(),
});
export type RadiologyAssistResult = z.infer<typeof radiologyAssistResultSchema>;

export const radiologyReportSchema = z.object({
	id: z.string().uuid(),
	order_id: z.string().uuid(),
	technique: z.string().nullable(),
	comparison: z.string().nullable(),
	findings: z.string().nullable(),
	impression: z.string().nullable(),
	recommendations: z.string().nullable(),
	diagnosis_codes: z.array(radiologyDiagnosisCodeSchema),
	/** Radiologist's explicit critical declaration (manual flag + reason). */
	manual_critical: z.boolean().optional(),
	manual_critical_reason: z.string().nullable().optional(),
	finalized_at: z.string().nullable(),
	finalized_by_member_id: z.string().uuid().nullable(),
	addendum: z.string().nullable(),
	addendum_at: z.string().nullable(),
	addendum_by_member_id: z.string().uuid().nullable(),
	/** Second-read peer sign-off. */
	verified_at: z.string().nullable().optional(),
	verified_by_member_id: z.string().uuid().nullable().optional(),
	verified_by_name: z.string().nullable().optional(),
	/** Ordering doctor's results-inbox acknowledgment (migration 0150). */
	reviewed_at: z.string().nullable().optional(),
	reviewed_by_member_id: z.string().uuid().nullable().optional(),
	/** Optimistic-lock counter — send it back as expected_version on the next save. */
	version: z.number().int().default(1),
	created_at: z.string(),
	updated_at: z.string(),
});
export type RadiologyReportApi = z.infer<typeof radiologyReportSchema>;

/** A prior study of the same patient, for the report's comparison picker. */
export const priorStudySchema = z.object({
	id: z.string().uuid(),
	modality: radiologyModalitySchema,
	study_name: z.string(),
	accession_number: z.string().nullable(),
	reported_at: z.string().nullable(),
	impression: z.string().nullable(),
});
export type PriorStudyApi = z.infer<typeof priorStudySchema>;

/** Set/clear a follow-up recall on a study (in-app reminder via cron). */
export const setRadiologyRecallInputSchema = z.object({
	recall_at: z.string().datetime().nullable(),
});
export type SetRadiologyRecallInput = z.infer<
	typeof setRadiologyRecallInputSchema
>;

// ===== Image =====

export const radiologyImagePresignInputSchema = z.object({
	content_type: z.enum([
		"image/jpeg",
		"image/png",
		"image/webp",
		"application/pdf",
	]),
	byte_size: z
		.number()
		.int()
		.positive()
		.max(25 * 1024 * 1024), // 25 MB cap
	label: z.string().max(120).optional(),
});
export type RadiologyImagePresignInput = z.infer<
	typeof radiologyImagePresignInputSchema
>;

export const radiologyImagePresignResponseSchema = z.object({
	upload_url: z.string().url(),
	r2_key: z.string(),
	expires_in: z.number().int(),
});
export type RadiologyImagePresignResponse = z.infer<
	typeof radiologyImagePresignResponseSchema
>;

export const confirmRadiologyImageInputSchema = z.object({
	r2_key: z.string().min(1).max(500),
	content_type: z.string().min(1).max(120),
	byte_size: z.number().int().positive().nullable().optional(),
	label: z.string().max(120).nullable().optional(),
});
export type ConfirmRadiologyImageInput = z.infer<
	typeof confirmRadiologyImageInputSchema
>;

export const radiologyImageSchema = z.object({
	id: z.string().uuid(),
	order_id: z.string().uuid(),
	r2_key: z.string(),
	content_type: z.string(),
	byte_size: z.number().nullable(),
	label: z.string().nullable(),
	display_order: z.number(),
	// Non-null only on DICOM-bridge preview renders (0154) — the UI badges
	// these as «DICOM» (the original .dcm sits in the order's DICOM panel).
	sop_instance_uid: z.string().nullable().optional(),
	created_at: z.string(),
	// Presigned download URL, generated on read. Short-lived; client should
	// refetch the list if the page sits open more than ~5 minutes.
	download_url: z.string().url().optional(),
});
export type RadiologyImageApi = z.infer<typeof radiologyImageSchema>;

// ===== DICOM / PACS =====

/**
 * Metadata the browser extracts from a DICOM Part-10 file (dicom-parser) and
 * sends on confirm. It is an INDEX only — the viewer always re-reads the
 * authoritative bytes from R2, so a bad tag can only mis-sort, never corrupt.
 */
export const dicomInstanceMetadataSchema = z.object({
	study_instance_uid: z.string().min(1).max(128),
	series_instance_uid: z.string().min(1).max(128),
	sop_instance_uid: z.string().min(1).max(128),
	modality: z.string().max(16).nullable().optional(),
	series_number: z.number().int().nullable().optional(),
	instance_number: z.number().int().nullable().optional(),
	number_of_frames: z.number().int().min(1).max(100_000).default(1),
	rows: z.number().int().nullable().optional(),
	columns: z.number().int().nullable().optional(),
	series_description: z.string().max(256).nullable().optional(),
	study_description: z.string().max(256).nullable().optional(),
	transfer_syntax_uid: z.string().max(128).nullable().optional(),
});
export type DicomInstanceMetadata = z.infer<typeof dicomInstanceMetadataSchema>;

export const dicomPresignInputSchema = z.object({
	// CT/MR DICOM instances can be large; allow up to 200 MB per instance.
	byte_size: z
		.number()
		.int()
		.positive()
		.max(200 * 1024 * 1024),
});
export type DicomPresignInput = z.infer<typeof dicomPresignInputSchema>;

export const dicomPresignResponseSchema = z.object({
	upload_url: z.string().url(),
	r2_key: z.string(),
	expires_in: z.number().int(),
});
export type DicomPresignResponse = z.infer<typeof dicomPresignResponseSchema>;

export const confirmDicomInstanceInputSchema =
	dicomInstanceMetadataSchema.extend({
		r2_key: z.string().min(1).max(500),
		byte_size: z.number().int().positive().nullable().optional(),
	});
export type ConfirmDicomInstanceInput = z.infer<
	typeof confirmDicomInstanceInputSchema
>;

export const dicomInstanceApiSchema = z.object({
	id: z.string().uuid(),
	sop_instance_uid: z.string(),
	instance_number: z.number().nullable(),
	number_of_frames: z.number(),
	r2_key: z.string(),
	transfer_syntax_uid: z.string().nullable(),
	// Presigned download URL (short-lived); the cornerstone wadouri loader reads it.
	download_url: z.string().url().optional(),
});
export type DicomInstanceApi = z.infer<typeof dicomInstanceApiSchema>;

export const dicomSeriesApiSchema = z.object({
	series_instance_uid: z.string(),
	series_number: z.number().nullable(),
	series_description: z.string().nullable(),
	modality: z.string().nullable(),
	instances: z.array(dicomInstanceApiSchema),
});
export type DicomSeriesApi = z.infer<typeof dicomSeriesApiSchema>;

export const dicomStudyApiSchema = z.object({
	study_instance_uid: z.string(),
	study_description: z.string().nullable(),
	series: z.array(dicomSeriesApiSchema),
});
export type DicomStudyApi = z.infer<typeof dicomStudyApiSchema>;

// ===== Cross-facility referral (radiology-center facing) =====

/**
 * A radiology center the calling clinic is linked to (clinic-facing picker
 * for routing studies). Same shape as the lab/pharmacy linked picker.
 */
export const linkedRadiologySchema = z.object({
	id: z.string().uuid(),
	name: bilingualStringSchema,
	slug: z.string(),
});
export type LinkedRadiologyApi = z.infer<typeof linkedRadiologySchema>;

/**
 * A study routed to a radiology center's incoming worklist. Extends the
 * normal order with the referring clinic name + report/image indicators so
 * the center's worklist needs no second round-trip.
 */
export const incomingRadiologyOrderSchema = radiologyOrderSchema.extend({
	clinic_name: z.string(),
	has_report: z.boolean(),
	report_finalized: z.boolean(),
	image_count: z.number().int(),
	/** The link's default commission % (prefills the accept-time override). */
	link_commission_pct: z.string().nullable().optional(),
});
export type IncomingRadiologyOrderApi = z.infer<
	typeof incomingRadiologyOrderSchema
>;

// ===== Pure helpers =====

/**
 * Keyword-based detector for "this impression sounds critical". Used by the
 * future critical-findings notification flow. Returns null when no hit, or
 * the matched canonical keyword when one fires — caller decides what to do
 * with it (e.g. open the physician-contact log entry).
 *
 * Keep this list narrow: the ACR's actionable findings list is the source.
 * Casting a wider net produces alert fatigue, which is worse than missing
 * the occasional borderline call.
 */
const CRITICAL_KEYWORDS_EN = [
	"pulmonary embolism",
	"aortic dissection",
	"intracranial hemorrhage",
	"intracranial bleed",
	"epidural hematoma",
	"subdural hematoma",
	"subarachnoid hemorrhage",
	"tension pneumothorax",
	"free air",
	"bowel perforation",
	"mesenteric ischemia",
	"malignant",
	"mass suspicious",
	"cord compression",
	"testicular torsion",
	"ectopic pregnancy",
	"ruptured aneurysm",
] as const;

const CRITICAL_KEYWORDS_AR = [
	"انصمام رئوي",
	"تمزق الأبهر",
	"نزف داخل القحف",
	"نزف داخل الجمجمة",
	"ورم دموي",
	"استرواح صدري",
	"انثقاب",
	"خبيث",
	"كتلة مشبوهة",
	"انضغاط النخاع",
	"التواء الخصية",
	"حمل خارج الرحم",
] as const;

/**
 * Canonical matched_keyword for a radiologist's MANUAL critical declaration
 * (report.manual_critical = true). Routed through the exact same
 * critical-finding log + urgent-notification path as keyword detections, and
 * keyed on (order_id, matched_keyword) like any detected keyword so a
 * re-finalize stays idempotent.
 */
export const MANUAL_CRITICAL_KEYWORD = "تبليغ حرج يدوي";

// ===== Negation awareness =====
//
// «لا يوجد استرواح صدري» must NOT fire a critical alert (audit P1-rad-safety
// #1). A keyword mention is suppressed when, INSIDE the same clause (clauses
// split on sentence enders + commas), a negation marker appears within
// NEGATION_WINDOW words BEFORE it (لا يوجد / بدون / no / without / …) or a
// post-positive negation appears within the window AFTER it (ruled out /
// absent / مستبعد / "…: لا يوجد"). "Cannot exclude" style uncertainty phrases
// FORCE the flag — under-alerting is the dangerous direction.

const NEGATION_WINDOW = 6;

/** Clause boundaries: sentence enders, commas (Arabic + Latin), newlines. */
const CLAUSE_SPLIT_RE = /[.!?؟؛;,،\n\r]+/;

/** Negation markers that precede the finding (token-anchored phrases). */
const LEADING_NEGATION_MARKERS = [
	// Arabic
	"لا يوجد",
	"لا توجد",
	"ما يوجد",
	"ما توجد",
	"بدون",
	"دون",
	"عدم",
	"نفي",
	"خالي من",
	"خالية من",
	"خلو من",
	"سلبي لـ",
	"سلبي ل",
	// English (detector lowercases EN text before matching)
	"no",
	"without",
	"negative for",
	"ruled out",
	"absent",
	"free of",
	"no evidence of",
] as const;

/** Post-positive negations — they follow the finding ("PE ruled out"). */
const TRAILING_NEGATION_MARKERS = [
	// Arabic (checklist style «استرواح صدري: لا يوجد» + adjectives)
	"لا يوجد",
	"لا توجد",
	"غير موجود",
	"غير موجودة",
	"مستبعد",
	"مستبعدة",
	"منفي",
	"منفية",
	"تم نفيه",
	"تم نفيها",
	"سلبي",
	"سلبية",
	// English
	"ruled out",
	"is excluded",
	"excluded",
	"not seen",
	"not identified",
	"not visualized",
	"not demonstrated",
	"absent",
	"negative",
] as const;

/**
 * Uncertainty phrases that OVERRIDE negation: "pulmonary embolism cannot be
 * ruled out" / «لا يمكن استبعاد انصمام رئوي» is a positive-possibility call
 * and must still alert.
 */
const CANNOT_EXCLUDE_MARKERS = [
	"لا يمكن استبعاد",
	"لا يمكن نفي",
	"لا يستبعد",
	"يصعب استبعاد",
	"cannot",
	"can not",
	"can't",
	"could not",
	"couldn't",
] as const;

/** Token-anchored phrase containment over whitespace-joined words. */
function hasPhrase(text: string, phrase: string): boolean {
	return ` ${text} `.includes(` ${phrase} `);
}

/**
 * Is the keyword occurrence at [idx, idx+len) inside `clause` negated?
 * Looks NEGATION_WINDOW words back for leading markers and NEGATION_WINDOW
 * words forward for trailing markers. A bare «لا» only negates within 3 words
 * («لا استرواح صدري») — a wider window would swallow «لا تزال علامات …».
 */
function isNegatedMention(clause: string, idx: number, len: number): boolean {
	const beforeWords = clause.slice(0, idx).split(/\s+/).filter(Boolean);
	const beforeText = beforeWords.slice(-NEGATION_WINDOW).join(" ");
	const afterWords = clause
		.slice(idx + len)
		.split(/\s+/)
		.filter(Boolean);
	const afterText = afterWords.slice(0, NEGATION_WINDOW).join(" ");

	// "Cannot exclude" anywhere around the mention → always flag.
	for (const m of CANNOT_EXCLUDE_MARKERS) {
		if (hasPhrase(beforeText, m) || hasPhrase(afterText, m)) return false;
	}

	for (const m of LEADING_NEGATION_MARKERS) {
		if (hasPhrase(beforeText, m)) return true;
	}
	if (hasPhrase(beforeWords.slice(-3).join(" "), "لا")) return true;
	for (const m of TRAILING_NEGATION_MARKERS) {
		if (hasPhrase(afterText, m)) return true;
	}
	return false;
}

/**
 * True when at least ONE mention of `keyword` in `text` is NOT negated.
 * A doubly-mentioned keyword where one mention is negated and another isn't
 * («لا يوجد استرواح أيسر. يلاحظ استرواح صدري أيمن») still flags.
 */
function hasUnnegatedMention(text: string, keyword: string): boolean {
	for (const clause of text.split(CLAUSE_SPLIT_RE)) {
		let from = 0;
		let idx = clause.indexOf(keyword, from);
		while (idx !== -1) {
			if (!isNegatedMention(clause, idx, keyword.length)) return true;
			from = idx + keyword.length;
			idx = clause.indexOf(keyword, from);
		}
	}
	return false;
}

export function detectCriticalFinding(
	impression: string | null | undefined,
): string | null {
	if (!impression) return null;
	const lower = impression.toLowerCase();
	for (const kw of CRITICAL_KEYWORDS_EN) {
		if (lower.includes(kw) && hasUnnegatedMention(lower, kw)) return kw;
	}
	for (const kw of CRITICAL_KEYWORDS_AR) {
		if (impression.includes(kw) && hasUnnegatedMention(impression, kw))
			return kw;
	}
	return null;
}

/**
 * True when a status counts as "complete" for TAT / dashboard purposes.
 * `delivered` is the canonical terminal state; `reported` is also counted
 * because some shops never explicitly tick "delivered".
 */
export function isRadiologyOrderComplete(
	status: RadiologyOrderStatus,
): boolean {
	return status === "reported" || status === "delivered";
}

// ===== Critical findings (Sprint 2 #1) =====
//
// Reuses the lab notification vocabulary (method + status) so the dashboard
// banner + contact-log modal are shared UI. The trigger is narrative, so the
// snapshot is the matched keyword + impression excerpt + study identity.

export const radiologyCriticalFindingSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	order_id: z.string().uuid(),
	report_id: z.string().uuid().nullable(),
	patient_id: z.string().uuid(),

	// Snapshot — frozen at detection time
	accession_number: z.string().nullable(),
	modality: radiologyModalitySchema,
	study_name: z.string(),
	matched_keyword: z.string(),
	impression_excerpt: z.string().nullable(),

	status: criticalAlertStatusSchema,

	notified_at: z.string().nullable(),
	notified_by_member_id: z.string().uuid().nullable(),
	notification_method: notificationMethodSchema.nullable(),
	notification_recipient: z.string().nullable(),
	notification_notes: z.string().nullable(),

	acknowledged_at: z.string().nullable(),
	acknowledged_by_member_id: z.string().uuid().nullable(),

	created_at: z.string(),
	updated_at: z.string(),

	// Joins surfaced for the UI without a second round-trip
	patient_name: z.string().optional(),
	patient_phone: z.string().nullable().optional(),
	notified_by_name: z.string().nullable().optional(),
	acknowledged_by_name: z.string().nullable().optional(),
});
export type RadiologyCriticalFindingApi = z.infer<
	typeof radiologyCriticalFindingSchema
>;

export const listRadiologyCriticalFindingsQuerySchema = z.object({
	status: criticalAlertStatusSchema.optional(),
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
	limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListRadiologyCriticalFindingsQuery = z.infer<
	typeof listRadiologyCriticalFindingsQuerySchema
>;

export const markRadiologyCriticalFindingNotifiedInputSchema = z.object({
	method: notificationMethodSchema,
	recipient: z.string().min(1).max(200),
	notes: z.string().max(2_000).optional().nullable(),
});
export type MarkRadiologyCriticalFindingNotifiedInput = z.infer<
	typeof markRadiologyCriticalFindingNotifiedInputSchema
>;

// ===== Report templates (Sprint 2 #2) =====

export const radiologyTemplateSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: z.string(),
	modality: radiologyModalitySchema.nullable(),
	technique: z.string().nullable(),
	findings: z.string().nullable(),
	impression: z.string().nullable(),
	recommendations: z.string().nullable(),
	display_order: z.number(),
	created_at: z.string(),
	updated_at: z.string(),
});
export type RadiologyTemplateApi = z.infer<typeof radiologyTemplateSchema>;

export const upsertRadiologyTemplateInputSchema = z.object({
	name: z.string().min(1).max(120),
	modality: radiologyModalitySchema.nullable().optional(),
	technique: z.string().max(4_000).nullable().optional(),
	findings: z.string().max(16_000).nullable().optional(),
	impression: z.string().max(8_000).nullable().optional(),
	recommendations: z.string().max(4_000).nullable().optional(),
	display_order: z.number().int().min(0).max(9_999).optional(),
});
export type UpsertRadiologyTemplateInput = z.infer<
	typeof upsertRadiologyTemplateInputSchema
>;

// ===== TAT report (Sprint 2 #4) =====
//
// Read-only aggregation over radiology_orders.ordered_at → completed_at.
// Grouped by modality (a radiology order is a single study, so unlike the
// lab there's no per-test UNNEST). ISO 15189 §5.6.5 quality indicator.

export const radiologyTatReportQuerySchema = z.object({
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
});
export type RadiologyTatReportQuery = z.infer<
	typeof radiologyTatReportQuerySchema
>;

export const radiologyTatModalityRowSchema = z.object({
	modality: radiologyModalitySchema,
	count: z.number().int(),
	median_hours: z.number(),
	p95_hours: z.number(),
	p99_hours: z.number(),
	min_hours: z.number(),
	max_hours: z.number(),
});
export type RadiologyTatModalityRow = z.infer<
	typeof radiologyTatModalityRowSchema
>;

export const radiologyTatOverallSchema = z.object({
	order_count: z.number().int(),
	median_hours: z.number().nullable(),
	p95_hours: z.number().nullable(),
});
export type RadiologyTatOverall = z.infer<typeof radiologyTatOverallSchema>;

export const radiologyTatReportResponseSchema = z.object({
	from: z.string().datetime(),
	to: z.string().datetime(),
	overall: radiologyTatOverallSchema,
	per_modality: z.array(radiologyTatModalityRowSchema),
});
export type RadiologyTatReportResponse = z.infer<
	typeof radiologyTatReportResponseSchema
>;

// ===== Public report page (Sprint 2 #5) =====
//
// Patient-facing view at /rad/<share_token>. Possession of the unguessable
// token IS the authorization (same model as /lab and /rx). Only a FINALIZED
// report is ever exposed — drafts 404 — so a patient never sees an unsigned
// study.

export const publicRadiologyImageSchema = z.object({
	id: z.string().uuid(),
	content_type: z.string(),
	label: z.string().nullable(),
	view_url: z.string().nullable(),
});
export type PublicRadiologyImage = z.infer<typeof publicRadiologyImageSchema>;

export const publicRadiologyReportSchema = z.object({
	study: z.object({
		modality: radiologyModalitySchema,
		body_region: z.string(),
		study_name: z.string(),
		accession_number: z.string().nullable(),
		ordered_at: z.string(),
	}),
	report: z.object({
		technique: z.string().nullable(),
		comparison: z.string().nullable(),
		findings: z.string().nullable(),
		impression: z.string().nullable(),
		recommendations: z.string().nullable(),
		diagnosis_codes: z.array(radiologyDiagnosisCodeSchema),
		addendum: z.string().nullable(),
		finalized_at: z.string(),
	}),
	images: z.array(publicRadiologyImageSchema),
	patient: z.object({ display_name: z.string() }),
	radiologist: z.object({ display_name: z.string().nullable() }),
	clinic: z.object({
		name: bilingualStringSchema,
		slug: z.string(),
		logo_url: z.string().nullable(),
		primary_color: z.string().nullable(),
	}),
	/**
	 * Performing-center provenance: set when this study was produced at a linked
	 * center (referral). The patient report shows "performed at X". Null = in-house.
	 */
	performed_by: z.string().nullable(),
	/** Second-read peer sign-off name (shown as "verified by"). */
	verified_by: z.string().nullable().optional(),
});
export type PublicRadiologyReportApi = z.infer<
	typeof publicRadiologyReportSchema
>;

/** Aggregated today-counts for the radiology dashboard (computed server-side). */
export interface RadiologyDashboardStats {
	pending: number;
	reported: number;
	delivered: number;
	rejected: number;
}

// ===== DICOM bridge (on-prem radiology bridge agent) =====
//
// The radiology bridge agent (tools/radiology-bridge-agent) runs inside the
// imaging center: it serves the Modality Worklist (C-FIND MWL) to modalities
// and receives studies over C-STORE, then forwards each instance to Klyra —
// original .dcm to the PACS index + a rendered PNG preview into the normal
// image gallery. Auth: per-instrument bridge token (same path as the LIS
// bridge, hashed at rest since 0152).

/** One row the bridge serves to modalities as an MWL scheduled procedure. */
export const bridgeRadiologyWorklistItemSchema = z.object({
	order_id: z.string().uuid(),
	accession_number: z.string(),
	modality: z.string(),
	study_name: z.string(),
	body_region: z.string(),
	urgency: z.string(),
	/** ISO datetime when scheduled; null = ad-hoc walk-in (use ordered_at). */
	scheduled_at: z.string().nullable(),
	ordered_at: z.string(),
	patient: z.object({
		full_name: z.string(),
		/** Klyra MRN — becomes DICOM PatientID. */
		mrn: z.string(),
		/** 'male' | 'female' | other — agent maps to DICOM M/F/''. */
		gender: z.string(),
		/** YYYY-MM-DD or null. */
		date_of_birth: z.string().nullable(),
	}),
});
export type BridgeRadiologyWorklistItem = z.infer<
	typeof bridgeRadiologyWorklistItemSchema
>;

export const bridgeRadiologyWorklistResponseSchema = z.object({
	orders: z.array(bridgeRadiologyWorklistItemSchema),
});
export type BridgeRadiologyWorklistResponse = z.infer<
	typeof bridgeRadiologyWorklistResponseSchema
>;

/**
 * Bridge asks for a presigned PUT before uploading one file (the original
 * .dcm or the rendered preview). Order is matched by accession_number
 * (fallback: explicit order_id) so the key lands under the right tenant
 * prefix. Size caps mirror the staff-side presign endpoints.
 */
export const bridgeRadiologyPresignInputSchema = z
	.object({
		order_id: z.string().uuid().optional(),
		accession_number: z.string().min(1).max(80).optional(),
		kind: z.enum(["dicom", "preview"]),
		byte_size: z
			.number()
			.int()
			.positive()
			.max(200 * 1024 * 1024),
		/** Preview only (dicom is always application/dicom). */
		content_type: z.enum(["image/png", "image/jpeg"]).optional(),
	})
	.refine((v) => v.order_id != null || v.accession_number != null, {
		message: "order_id or accession_number required",
	})
	.refine((v) => v.kind !== "preview" || v.byte_size <= 25 * 1024 * 1024, {
		message: "preview too large (max 25 MB)",
	});
export type BridgeRadiologyPresignInput = z.infer<
	typeof bridgeRadiologyPresignInputSchema
>;

export const bridgeRadiologyPresignResponseSchema = z.object({
	upload_url: z.string().url(),
	r2_key: z.string(),
	order_id: z.string().uuid(),
	accession_number: z.string().nullable(),
	expires_in: z.number().int(),
});
export type BridgeRadiologyPresignResponse = z.infer<
	typeof bridgeRadiologyPresignResponseSchema
>;

/**
 * One received SOP instance inside a study ingest call. The bridge already
 * PUT the bytes to R2 (via the presign endpoint) and now commits the index
 * rows. dcm_r2_key → radiology_dicom_instances (cornerstone viewer);
 * preview_r2_key → radiology_images (normal gallery, badged «DICOM»).
 * Either may be absent (e.g. preview skipped for unsupported photometric
 * interpretation), but at least one must be present.
 */
export const bridgeDicomInstanceSchema = z
	.object({
		sop_instance_uid: z.string().min(1).max(128),
		series_instance_uid: z.string().min(1).max(128),
		series_number: z.number().int().nullable().optional(),
		instance_number: z.number().int().nullable().optional(),
		number_of_frames: z.number().int().min(1).max(100_000).default(1),
		rows: z.number().int().nullable().optional(),
		columns: z.number().int().nullable().optional(),
		series_description: z.string().max(256).nullable().optional(),
		transfer_syntax_uid: z.string().max(128).nullable().optional(),
		dcm_r2_key: z.string().min(1).max(500).nullable().optional(),
		dcm_byte_size: z.number().int().positive().nullable().optional(),
		preview_r2_key: z.string().min(1).max(500).nullable().optional(),
		preview_byte_size: z.number().int().positive().nullable().optional(),
		preview_content_type: z
			.enum(["image/png", "image/jpeg"])
			.default("image/png"),
		label: z.string().max(120).nullable().optional(),
	})
	.refine((v) => v.dcm_r2_key != null || v.preview_r2_key != null, {
		message: "dcm_r2_key or preview_r2_key required",
	});
export type BridgeDicomInstance = z.infer<typeof bridgeDicomInstanceSchema>;

export const bridgeStudyIngestSchema = z
	.object({
		order_id: z.string().uuid().optional(),
		accession_number: z.string().min(1).max(80).optional(),
		study_instance_uid: z.string().min(1).max(128),
		study_description: z.string().max(256).nullable().optional(),
		modality: z.string().max(16).nullable().optional(),
		instances: z.array(bridgeDicomInstanceSchema).min(1).max(200),
	})
	.refine((v) => v.order_id != null || v.accession_number != null, {
		message: "order_id or accession_number required",
	});
export type BridgeStudyIngest = z.infer<typeof bridgeStudyIngestSchema>;

export const bridgeStudyIngestResponseSchema = z.object({
	order_id: z.string().uuid(),
	accession_number: z.string().nullable(),
	study_instance_uid: z.string(),
	/** New SOP-instance index rows created (re-sends count as updated). */
	instances_created: z.number().int(),
	instances_updated: z.number().int(),
	previews_created: z.number().int(),
	previews_updated: z.number().int(),
	/** True when this call moved the order pending → in_progress. */
	order_advanced: z.boolean(),
	/**
	 * Warnings the bridge should log for review:
	 *   - 'order_reported'/'order_delivered'/'order_cancelled'/'order_rejected'
	 *     — images arrived for an order already past in_progress.
	 *   - 'study_uid_mismatch' — order already carries a different
	 *     study_instance_uid (kept; new value NOT overwritten).
	 */
	warnings: z.array(z.string()),
});
export type BridgeStudyIngestResponse = z.infer<
	typeof bridgeStudyIngestResponseSchema
>;
